import { LOCALES, type MailSettings, type MailStatus } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Spinner,
  Textarea,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, Send } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { useEnabledLocales } from '@/features/language/language-switcher';
import { formatDate } from '@/features/shared/labels';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

/**
 * Admin → E-mail → Status (docs/partial/email-and-support-spec.md 6.7): is everything set up,
 * does a test mail leave, and the brand values every mail shares.
 */
export function AdminMailPage() {
  const { t } = useTranslation();
  const status = useQuery({
    queryKey: queryKeys.admin.mailStatus,
    queryFn: () => api.admin.mail.status(),
  });
  return (
    <AdminPage section="mail" description={t('admin.mail.description')}>
      {status.isLoading && <Spinner label={t('app.loading')} />}
      {status.error && <Alert variant="destructive">{describeError(status.error)}</Alert>}
      {status.data && (
        <div className="space-y-6">
          <StatusChecks status={status.data} />
          <TestMail />
          <BrandSettings />
        </div>
      )}
    </AdminPage>
  );
}

function Check({ ok, label, detail }: { ok: boolean; label: string; detail?: ReactNode }) {
  const { t } = useTranslation();
  const Icon = ok ? CheckCircle2 : CircleAlert;
  return (
    <li className="flex items-start gap-2 py-2">
      <Icon
        aria-hidden
        className={ok ? 'mt-0.5 size-4 text-success' : 'mt-0.5 size-4 text-warning'}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {label}
          <span className="sr-only"> – {t(ok ? 'admin.mail.ok' : 'admin.mail.attention')}</span>
        </p>
        {detail && <p className="break-words text-sm text-muted-foreground">{detail}</p>}
      </div>
    </li>
  );
}

function StatusChecks({ status }: { status: MailStatus }) {
  const { t, i18n } = useTranslation();
  const resend = status.transport === 'resend';
  return (
    <section aria-labelledby="mail-status-title" data-testid="mail-status">
      <h2 id="mail-status-title" className="mb-2 text-lg font-semibold">
        {t('admin.mail.statusTitle')}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {(
          [
            ['sent24h', status.counts.sent24h],
            ['failed24h', status.counts.failed24h],
            ['suppressed', status.counts.suppressed],
            ['failedWebhookEvents', status.counts.failedWebhookEvents],
          ] as const
        ).map(([key, value]) => (
          <Card key={key}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t(`admin.mail.counts.${key}`)}</p>
              <p className="mt-1 text-xl font-semibold">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <ul className="mt-4 divide-y rounded-lg border bg-card px-4">
        <Check
          ok={status.transport !== 'log'}
          label={t('admin.mail.transport')}
          detail={
            <>
              <Badge variant="outline" data-testid="mail-transport">
                {status.transport}
              </Badge>{' '}
              {status.transport === 'log' && t('admin.mail.transportLog')}
            </>
          }
        />
        {resend && (
          <Check
            ok={status.apiKeySet}
            label={t('admin.mail.apiKey')}
            detail={t(status.apiKeySet ? 'admin.mail.secretSet' : 'admin.mail.secretMissing')}
          />
        )}
        {resend && (
          <Check
            ok={status.webhookSecretSet}
            label={t('admin.mail.webhookSecret')}
            detail={t(
              status.webhookSecretSet ? 'admin.mail.secretSet' : 'admin.mail.secretMissing',
            )}
          />
        )}
        <Check
          ok
          label={t('admin.mail.senders')}
          detail={`${status.fromAccount} · ${status.fromSupport}`}
        />
        <Check
          ok={status.appBaseUrlIsHttps}
          label={t('admin.mail.linkDomain')}
          detail={
            <>
              {status.appBaseUrl}
              {!status.appBaseUrlIsHttps && <> – {t('admin.mail.linkDomainHttp')}</>}
            </>
          }
        />
        <Check
          ok={status.requireEmailVerification}
          label={t('admin.mail.verification')}
          detail={t(
            status.requireEmailVerification
              ? 'admin.mail.verificationOn'
              : 'admin.mail.verificationOff',
          )}
        />
        {resend && (
          <Check
            ok={status.lastWebhookEventAt !== null}
            label={t('admin.mail.webhook')}
            detail={
              <>
                <code className="text-xs">{status.webhookUrl}</code>
                <br />
                {status.lastWebhookEventAt
                  ? t('admin.mail.webhookLast', {
                      date: formatDate(status.lastWebhookEventAt, i18n.language, true),
                    })
                  : t('admin.mail.webhookNone')}
              </>
            }
          />
        )}
        {resend && (
          <Check
            ok={!status.trackingWarning}
            label={t('admin.mail.tracking')}
            detail={t(status.trackingWarning ? 'admin.mail.trackingOn' : 'admin.mail.trackingOff')}
          />
        )}
        <Check
          ok={status.supportInboxEnabled && status.supportInboundAddress !== null}
          label={t('admin.mail.inbox')}
          detail={
            status.supportInboxEnabled
              ? (status.supportInboundAddress ?? t('admin.mail.inboxNoAddress'))
              : t('admin.mail.inboxOff')
          }
        />
      </ul>
    </section>
  );
}

function TestMail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const test = useMutation({
    mutationFn: () => api.admin.mail.test(),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'mail'] });
      if (result.ok) toast.success(t('admin.mail.testSent', { to: result.to }));
    },
    onError: (e) => toastError(e),
  });
  return (
    <section aria-labelledby="mail-test-title" className="rounded-lg border bg-card p-4">
      <h2 id="mail-test-title" className="text-lg font-medium">
        {t('admin.mail.testTitle')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('admin.mail.testDescription')}</p>
      <Button
        className="mt-3"
        variant="outline"
        loading={test.isPending}
        onClick={() => test.mutate()}
        data-testid="mail-test-send"
      >
        <Send aria-hidden /> {t('admin.mail.testButton')}
      </Button>
      {test.data && !test.data.ok && (
        <Alert variant="destructive" className="mt-3" data-testid="mail-test-error">
          {t('admin.mail.testFailed')} {test.data.error}
        </Alert>
      )}
    </section>
  );
}

function BrandSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const locales = useEnabledLocales();
  const settings = useQuery({
    queryKey: queryKeys.admin.mailSettings,
    queryFn: () => api.admin.mail.settings(),
  });
  const [form, setForm] = useState<MailSettings | null>(null);
  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (value: MailSettings) =>
      api.admin.mail.updateSettings({
        appName: value.appName.trim(),
        logoUrl: value.logoUrl?.trim() || null,
        primaryColor: value.primaryColor,
        footerLines: Object.fromEntries(
          Object.entries(value.footerLines).map(([locale, lines]) => [
            locale,
            (lines ?? []).map((line) => line.trim()).filter(Boolean),
          ]),
        ),
        imprintUrl: value.imprintUrl?.trim() || null,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.admin.mailSettings, next);
      toast.success(t('admin.mail.brandSaved'));
    },
    onError: (e) => toastError(e),
  });
  if (!form)
    return settings.error ? (
      <Alert variant="destructive">{describeError(settings.error)}</Alert>
    ) : null;
  const set = (patch: Partial<MailSettings>) => setForm({ ...form, ...patch });
  return (
    <section aria-labelledby="mail-brand-title" className="rounded-lg border bg-card p-4">
      <h2 id="mail-brand-title" className="text-lg font-medium">
        {t('admin.mail.brandTitle')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('admin.mail.brandDescription')}</p>
      <form
        className="mt-4 grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <div>
          <Label htmlFor="brand-name">{t('admin.mail.appName')}</Label>
          <Input
            id="brand-name"
            className="mt-1"
            value={form.appName}
            maxLength={60}
            required
            onChange={(e) => set({ appName: e.target.value })}
            data-testid="mail-brand-name"
          />
        </div>
        <div>
          <Label htmlFor="brand-color">{t('admin.mail.primaryColor')}</Label>
          <div className="mt-1 flex items-center gap-2">
            <input
              id="brand-color"
              type="color"
              className="h-10 w-14 rounded-md border bg-background p-1"
              value={form.primaryColor}
              onChange={(e) => set({ primaryColor: e.target.value })}
            />
            <code className="text-sm">{form.primaryColor}</code>
          </div>
        </div>
        <div>
          <Label htmlFor="brand-logo">{t('admin.mail.logoUrl')}</Label>
          <Input
            id="brand-logo"
            type="url"
            className="mt-1"
            placeholder="https://"
            value={form.logoUrl ?? ''}
            onChange={(e) => set({ logoUrl: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="brand-imprint">{t('admin.mail.imprintUrl')}</Label>
          <Input
            id="brand-imprint"
            type="url"
            className="mt-1"
            placeholder="https://"
            value={form.imprintUrl ?? ''}
            onChange={(e) => set({ imprintUrl: e.target.value })}
          />
        </div>
        {locales.map((locale) => (
          <div key={locale}>
            <Label htmlFor={`brand-footer-${locale}`}>
              {t('admin.mail.footerLines', { language: LOCALES[locale].nativeName })}
            </Label>
            <Textarea
              id={`brand-footer-${locale}`}
              className="mt-1"
              rows={3}
              value={(form.footerLines[locale] ?? []).join('\n')}
              onChange={(e) =>
                set({
                  footerLines: {
                    ...form.footerLines,
                    [locale]: e.target.value
                      .split('\n')
                      .map((line) => line.slice(0, 200))
                      .slice(0, 6),
                  },
                })
              }
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Button type="submit" loading={save.isPending} data-testid="mail-brand-save">
            {t('common.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
